import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PairLink } from '@root/entities/pair/ui/common'
import { PairElementContextProvider } from '@root/entities/pair/ui/common/pair-element-context'
import { AppDataTable } from '@root/features/app-data-table/app-data-table'
import { usePageScrollElement } from '@root/shared/layout/page-scroll-context'
import {
  findScrollableAncestor,
  scrollAnchorToScrollportTop,
} from '@root/shared/lib/dom/find-scrollable-ancestor'
import { DataCounterButton } from '@root/shared/ui-kit'
import { TOPBAR_HEIGHT } from '@root/shared/ui-kit/utils/page-layout'

import { exploreTablePeriodAtom } from '../../atoms/explore-table'

import { ExploreTableEmpty } from './explore-table-empty'

import type { ExploreTableColumnId } from './explore-column'
import type { ExploreTableContext } from './explore-table-types'
import type { ExploreTableFeedType } from '../../atoms/explore-table'
import type { ExploreTableBaseModel } from '../../atoms/explore-table/reatom-explore-table-base'
import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type ExploreTableProps = {
  tableModel: ExploreTableBaseModel
  feedType: ExploreTableFeedType
} & ClassNameProps

export const EXPLORE_TABLE_HEADER_HEIGHT = 50

export const ExploreTable: React.FC<ExploreTableProps> = ({
  className,
  tableModel,
  feedType,
}) => {
  const { t } = useTranslation()
  const [period] = useAtom(exploreTablePeriodAtom)
  const [paused, setPaused] = useAtom(tableModel.paused)
  const [buffedCount] = useAtom(tableModel.buffedCount)
  const tableAreaRef = useRef<HTMLDivElement>(null)

  const ctxValue = useMemo(() => ({ period, feedType }), [period, feedType])

  const handleRetry = useAction(tableModel.refresh)

  useEffect(() => {
    const tableArea = tableAreaRef.current
    const scrollEl = findScrollableAncestor(tableArea)
    if (!(tableArea && scrollEl instanceof HTMLElement)) {
      return
    }

    const handleScroll = () => {
      const scrollRect = scrollEl.getBoundingClientRect()
      const tableRect = tableArea.getBoundingClientRect()
      setPaused(tableRect.top < scrollRect.top + 20)
    }

    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      scrollEl.removeEventListener('scroll', handleScroll)
    }
  }, [setPaused])

  const handleNewDataClick = () => {
    const tableArea = tableAreaRef.current
    if (!tableArea) {
      return
    }

    scrollAnchorToScrollportTop(
      tableArea,
      TOPBAR_HEIGHT + EXPLORE_TABLE_HEADER_HEIGHT,
    )
  }

  const customScrollParent = usePageScrollElement()

  const floatingActions =
    paused && buffedCount > 0 ? (
      <DataCounterButton
        counter={buffedCount}
        text={t('trade.table.new-pairs')}
        onClick={handleNewDataClick}
      />
    ) : undefined

  return (
    <Root className={className}>
      <TableArea ref={tableAreaRef}>
        <AppDataTable<PairModel, ExploreTableColumnId, ExploreTableContext>
          disableIndex={true}
          context={ctxValue}
          model={tableModel}
          rowHeight={60}
          customScrollParent={customScrollParent}
          increaseViewportBy={600}
          stickyHeaderTop={TOPBAR_HEIGHT + EXPLORE_TABLE_HEADER_HEIGHT}
          floatingActions={floatingActions}
          renderBackgroundLink={(args) => <PairLinkWrapper pair={args.data} />}
          renderRow={({ data, element }) => (
            <PairElementContextProvider pair={data} useLinkForBadge={true}>
              {element}
            </PairElementContextProvider>
          )}
          renderEmptyMessage={() => (
            <ExploreTableEmpty onRetry={handleRetry} intent="empty" />
          )}
          renderError={() => (
            <ExploreTableEmpty onRetry={handleRetry} intent="error" />
          )}
        />
      </TableArea>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
`

const TableArea = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
`

const PairLinkWrapper = styled(PairLink<PairModel>)`
  position: absolute;
  inset: 0;
  z-index: 0;
`
